// The redeem side of forecast COPY links: acquire the secret, ASK what is on offer,
// let the recipient accept or decline, and create the copy only if they accept.
//
// TWO POSTS, AND NOTHING IS CREATED BETWEEN THEM. `POST …/preview` writes nothing
// (server/handlers/copyLinks.js:801) and answers what the link holds; `POST …/redeem`
// is the only call that creates anything, and it fires from ACCEPT and nowhere else.
// So the screen a recipient lands on is a decision — this forecast, from this
// person — rather than a fait accompli they discover afterwards.
//
// AND THE HAND-OFF IS ASYMMETRIC — the one part of this file that reads like an
// inconsistency and is not:
//
//   fresh copy (ACCEPT → 201)   `onRedeemed` fires here, automatically, exactly once
//   already had it              `onRedeemed` fires only from `openCopy` (the [OK] button)
//
// Consent for a fresh copy was given a moment earlier by pressing ACCEPT, so arriving
// at the copy is the thing that was asked for and a confirming click is friction. The
// already-had screen instead carries NEWS — nothing was created, the copy predates this
// visit — and the one-shot version of this hook navigated past exactly that kind of
// screen in the same tick as `setPhase("done")`, which is why nobody could read a word
// of it. So the surviving automatic navigation is downstream of an explicit decision
// rather than of merely opening a URL, which is the objection this whole route answers.
//
// The branch keys off the SERVER's reply, not off which control was pressed: an ACCEPT
// that comes back `alreadyRedeemed` (two tabs, or a reload mid-flight) waits for [OK]
// too. Two tests hold the halves apart — "hands off by itself on a fresh copy, EXACTLY
// once" and "reaches an already-redeemed copy without POSTing a redeem at all" — and
// both assert the navigation log as an exact array, because a truthiness check cannot
// tell one navigation from two.
//
// DECLINING IS CLIENT-SIDE ONLY. `reject` sends nothing and records nothing — there
// is no server-side notion of a refused link, deliberately: the owner never learns
// who looked. The link therefore stays exactly as live as it was, and the declined
// screen has to say so, because the recipient's only recovery is to reopen the URL
// they were sent. It must not imply the link is now dead, and it offers no
// "un-decline", because there is nothing to undo.
//
// Lives in a hook rather than inside RedeemCopyRoute.jsx
// (where the plan put it, plan lines 687-752) for two independent reasons:
//
//  - hooks/ is where stateful behaviour and effects belong (chatboard/CLAUDE.md,
//    "Frontend conventions"), and
//  - `renderToStaticMarkup` — the only React renderer this app has, since the app
//    vitest project runs in the `node` environment with no jsdom and no
//    @testing-library — DOES NOT RUN EFFECTS. An effect inside the component is
//    therefore untestable here; a hook is testable with the slot renderer
//    hooks/__tests__/useCopyLinks.test.js established.
//
// It routes through lib/apiClient rather than `fetch` (the plan's `fetchImpl`),
// which is not a style preference: the `strayFetch` ratchet counter
// (app/scripts/ratchet.mjs, direction lte, goal 0) fails CI on a bare `fetch(`
// outside that layer, and — far more importantly — apiClient's 401 handling IS THE
// AUTH ROUND TRIP this whole file is built around. A 401 there redirects to
// `/auth/login?returnTo=<pathname+search>` and returns a never-resolving promise
// (apiClient.js:59-83), so nothing below the await runs, the stashed secret is left
// intact, and the recipient comes back to the same `/copy/:id` path with the secret
// recoverable from storage. Hand-rolling the POST would have to reimplement that.
// That holds for BOTH posts: preview 401s exactly as redeem does, which is why an
// unauthenticated arrival authenticates before ever seeing the offer.
//
// WORDING: the three messages below are the ones this file OWNS, and each names the
// thing in full — a FORECAST COPY LINK, never a bare "copy link", because on its own
// the phrase reads as a clipboard action rather than a forecast arriving in your list.
// Everything else the failure screens render is the server's own `{error}` string,
// passed through unchanged and still using the shorter form.
//
// THE SECRET GOES IN THE BODY AND NOWHERE ELSE — never a query string, never the
// OIDC `state`, never a `redirect_uri`, never a log line or an analytics call
// (§5.1). Note the redeem handler is equally careful on its side: it logs copyId
// and userId on every path and the secret on none.
//
// THE SECRET NOW HAS TO OUTLIVE A HUMAN DECISION, which the one-shot version never
// needed. It sits in three places with three lifetimes: the FRAGMENT (the arrival —
// scrubbed as soon as the stash holds it, and again once the preview answers),
// `sessionStorage` (survives the auth round trip, 10-minute TTL), and `secretRef` in
// memory for the length of this mount. The last one is what ACCEPT reads, so a
// storage that refused the stash still yields a working accept within the same page.
// The stash is dropped on all three terminal outcomes — a copy created, a copy
// already owned, and a decline — and losing it is recoverable in each: the link is
// either spent for this recipient (redeem is idempotent) or still live, and the URL
// they were sent still works.
//
// TWO SERVER FACTS SHAPE THE STATE MACHINE, and both are easy to get wrong:
//
//  1. REDEEM IS IDEMPOTENT per `(copy_id, recipient_user_id)`. A repeat answers
//     200 `{forecastId, alreadyRedeemed: true}` (copyLinks.js:850) instead of
//     creating a second copy, so a double-click, a reload, or a lost UNIQUE race
//     (:1013) is a SUCCESS. Treating a repeat as an error would tell someone their
//     copy failed while they own it. Preview reports the same state up front
//     (`alreadyRedeemed: true` with the existing `forecastId`), which is why an
//     arrival at a link they already took shows "you already have this" and a way
//     into it rather than an offer to take it again.
//  2. A FAILED REDEEM MUTATES NOTHING (§7.2). Every refusal above happens before
//     the transaction, or rolls it back, so there is never a partial copy. Nothing
//     rendered from here may hint at one ("partly copied", "may have created").
//
// And one that decides whether the stash survives: a 404 is AMBIGUOUS. The GOFF
// gate answers 404 as the first statement of the handler so the flag is not
// probeable (copyLinks.js:662 → :122), and an unknown copy id answers 404 too
// (:709). They need opposite handling — gating DEFERS a capability (the same URL
// redeems once the flag is back, since a gated redeem mutates nothing) — while an
// unknown id never becomes known — so the gate's exact body is matched, with a
// pass-through fallback if that string ever drifts.
import { beginIntent, withIntent } from "../lib/telemetry/instrument";
import { intentArgs } from "../lib/telemetry/uiIntents.js";
import { useCallback, useEffect, useRef, useState } from "react";
import { apiPost } from "../lib/apiClient.js";
import { clearFragment } from "../lib/copyLinks/url.js";
import { acquireCopySecret, clearStashedSecret } from "../lib/copyLinks/secretStash.js";
import { isCopyId } from "../lib/copyLinks/route.js";
import { recordUiEvent } from "../lib/logRingBuffer";
import {
  NETWORK_MESSAGE,
  SERVICE_UNAVAILABLE_MESSAGE,
  UNAVAILABLE_MESSAGE,
} from "./useCopyLinks.js";

/**
 * How long the declined screen stays before it leaves on its own.
 *
 * The recipient pressed REJECT, so the screen asks nothing — it confirms a decision
 * already made, and a button to acknowledge one's own choice is a click for nothing.
 * The only reason to wait at all is the sentence that matters: the forecast copy link
 * still works, so changing one's mind means reopening it. Six seconds is set to read
 * that once without unread text vanishing, and it is deliberately not tuned to the whole
 * paragraph — the rest is reassurance.
 *
 * Exported so a test pins the interval rather than asserting on a number it also
 * chose, and so the declined body and this timer cannot drift apart silently.
 */
export const DECLINE_DISMISS_MS = 6000;

/**
 * There is no secret in the fragment and none stashed. Not an error state — the
 * link simply arrived without the part that makes it work, which is what happens
 * when a chat client or a mail client truncates at the `#`.
 */
export const NO_SECRET_MESSAGE =
  "Please open the forecast copy link again from wherever you received it. The part " +
  "after the “#” is what makes the link work, and some apps drop it when a link is " +
  "forwarded.";

/**
 * The path segment is not a copy id, so no request could succeed.
 *
 * It names the recovery action as well as the problem, and that is not padding: this
 * branch SCRUBS the fragment (see the guard in `runPreview`), so the secret is no longer
 * visible anywhere. Without the second sentence the screen would be a dead end — the
 * user loses the only trace they could see and is told nothing to do about it.
 */
export const BAD_LINK_MESSAGE =
  "This does not look like a forecast copy link. Please open the forecast copy link " +
  "again from wherever you received it.";

/** Nothing usable came back and the server sent no message of its own. */
export const GENERIC_FAILURE_MESSAGE = "This forecast copy link could not be opened.";

/**
 * `gateClosed`'s exact body (server/handlers/copyLinks.js:122). A deliberate
 * hand-kept duplicate of the same literal in useCopyLinks.js:81, which is
 * module-private there — and the alternative (exporting it) would edit another
 * task's committed file. Best-effort by construction: if the string drifts we fall
 * back to passing the server's own message through, so a stale match costs wording
 * and nothing else.
 */
const GATE_404_ERROR = "not found";

/** The server's `{error}` string when it sent a usable one. */
function serverMessage(body) {
  const raw = body && typeof body.error === "string" ? body.error.trim() : "";
  return raw || null;
}

/**
 * Classify one refused preview or redeem.
 *
 * ONE classifier for both posts, because the server runs the SAME guard sequence in
 * both handlers — gated 404 / 401 / 400 / unconfigured 503 / no record 404 /
 * mismatch 403 / revoked-or-expired 410 / unreadable 409 (`previewCopyLink`'s
 * docstring states this as its contract). A second copy of these branches would
 * drift from this one on the first wording change.
 *
 * `retryable` decides whether a retry control is offered; `keepSecret` decides
 * whether the stash survives. They are NOT the same axis — a gated deployment is
 * pointless to retry now but will redeem the same URL later, so it keeps the
 * secret without offering a button.
 *
 * @param {number} status  HTTP status, or 0 for apiClient's network/abort sentinel
 * @param {unknown} body   the parsed reply body
 * @returns {{message: string, retryable: boolean, keepSecret: boolean}}
 */
export function classifyRedeemFailure(status, body) {
  const server = serverMessage(body);
  const passThrough = server || GENERIC_FAILURE_MESSAGE;

  // Offline / DNS / abort. Nothing was decided, so nothing is terminal.
  if (status === 0) return { message: NETWORK_MESSAGE, retryable: true, keepSecret: true };

  // The flag is on but this plane has no COPY_LINKS_* config (copyLinks.js:696),
  // or persistence is off (:667). Both are fixed by a deploy, not by the recipient.
  // Cause-neutral: redeem 503s for `!pool` OR `!cfg` too, and the RECIPIENT is a
  // stranger to this deployment — telling them about its env vars is doubly wrong.
  if (status === 503) {
    return { message: SERVICE_UNAVAILABLE_MESSAGE, retryable: true, keepSecret: true };
  }

  if (status === 404) {
    // The kill switch. Deferred, not revoked — keep the secret.
    if (server === GATE_404_ERROR) {
      return { message: UNAVAILABLE_MESSAGE, retryable: false, keepSecret: true };
    }
    // An unknown copy id. Never becomes known.
    return { message: passThrough, retryable: false, keepSecret: false };
  }

  // 409 is the whole family of "the record does not vouch for this content yet":
  // no generation recorded, a generation that moved under the fetch, an envelope
  // this plane cannot read, an unresolvable contract. Two of its messages say
  // "please try again" verbatim (copyLinks.js:40-41) — so offer that, and keep the
  // secret, because the next refresh-on-save records a generation and the retry
  // succeeds.
  if (status === 409) return { message: passThrough, retryable: true, keepSecret: true };

  // 500. Retryable in principle; the body is deliberately opaque ("internal_error"),
  // so do not show it.
  if (status >= 500) {
    return { message: GENERIC_FAILURE_MESSAGE, retryable: true, keepSecret: true };
  }

  // 410 revoked/expired, 403 secret mismatch, 400 malformed. Each carries
  // deliberate user-facing copy on the server side ("this copy link is no longer
  // active") and none of them can become a success, so pass the wording through and
  // drop the secret. Dropping it on 403 also matters for the server: a retained bad
  // secret would re-POST on every reload and keep firing
  // `copy_links.redeem.secret_mismatch`, which is the brute-force signal.
  return { message: passThrough, retryable: false, keepSecret: false };
}

/**
 * What the preview said, normalised for display.
 *
 * Every field is checked BY TYPE rather than passed through: this is the one place a
 * cross-plane reply reaches the screen, and `ownerLabel` in particular is
 * owner-authored text on a record that may predate the field entirely. A blank or
 * non-string label becomes null so the screen can say the sender is not recorded
 * instead of rendering an empty "from".
 *
 * `includeChat` is compared to `true`, not coerced: the server maps the record's
 * `"1"`/`"0"` cell to a real boolean (copyLinks.js:904), and `"0"` is truthy.
 *
 * @param {unknown} body  the parsed 200 body
 * @returns {{question: string|null, ownerLabel: string|null, expiresAt: string|null,
 *   includeChat: boolean, already: boolean}}
 */
function previewDetails(body) {
  const b = body && typeof body === "object" ? body : {};
  const label = typeof b.ownerLabel === "string" ? b.ownerLabel.trim() : "";
  return {
    question: typeof b.question === "string" && b.question.trim() ? b.question : null,
    ownerLabel: label || null,
    expiresAt: typeof b.expiresAt === "string" && b.expiresAt ? b.expiresAt : null,
    includeChat: b.includeChat === true,
    already: b.alreadyRedeemed === true,
  };
}

/**
 * Open one copy link: preview it, offer it, and redeem it only on ACCEPT.
 *
 * @param {object} args
 * @param {string} args.copyId  the `:id` path segment, NOT pre-validated
 * @param {object|null} [args.win]  window (injected, so nothing here can touch the
 *   real address bar in a test)
 * @param {Function} [args.apiPostImpl]  injectable for tests (lib/apiClient apiPost)
 * @param {(forecastId: string|null) => void} [args.onRedeemed]  the navigation, called
 *   ONLY from `openCopy` (the [OK] button) — never from a request handler. Receives
 *   the recipient's own forecast id, whether this visit created the copy or found one
 *   that already existed.
 * @param {(() => void)|null} [args.onDismiss]  leave without a copy. Called by the
 *   declined screen's auto-dismiss timer, and never with a forecast id — nothing was
 *   redeemed, which is exactly why it is not `onRedeemed`.
 * @param {object|null} [args.storage]  injectable sessionStorage
 * @param {() => number} [args.clock]  injectable epoch-ms source for the stash TTL
 * @returns {{
 *   phase: "opening"|"offer"|"creating"|"done"|"declined"|"needs-link"|"failed",
 *   details: {question: string|null, ownerLabel: string|null, expiresAt: string|null,
 *     includeChat: boolean, already: boolean}|null,
 *   already: boolean,
 *   message: string|null, retryable: boolean,
 *   accept: () => void, reject: () => void, openCopy: () => void, retry: () => void,
 * }}
 */
export function useRedeemCopy({
  copyId,
  win = typeof window === "undefined" ? null : window,
  apiPostImpl = apiPost,
  onRedeemed,
  onDismiss,
  storage,
  clock = Date.now,
}) {
  const [phase, setPhase] = useState("opening");
  // ONE state cell for `{message, retryable}` rather than two: they are always set
  // together, and two cells make a torn intermediate render possible (a new message
  // beside the previous attempt's retryability).
  const [note, setNote] = useState(null);
  // Likewise one cell for everything the preview said, `already` included — the
  // whole object is written once, from one reply.
  const [details, setDetails] = useState(null);

  // Held in a ref, not read as a dep. App.jsx may wire this as an inline arrow, and
  // a new identity per render would re-run the mount effect; on the re-run the
  // secret has already been cleared, so the screen would flip to "open the link
  // again" AFTER a successful redeem, and a navigate-driven re-render could loop the
  // POST. (The plan reached the same conclusion and spent an eslint-disable on it;
  // a ref needs none and stays correct if the caller's identity does change.)
  const onRedeemedRef = useRef(onRedeemed);
  onRedeemedRef.current = onRedeemed;
  // Same ref treatment, same reason: the route wires this as an inline `useCallback` and
  // the timer must call whatever is current when it FIRES, not what was passed when
  // REJECT was pressed.
  const onDismissRef = useRef(onDismiss);
  onDismissRef.current = onDismiss;

  // One PREVIEW per mount, whatever React does with effect identity — StrictMode
  // double-invokes effects in dev. Harmless for a write-nothing preview, but a
  // second one would flip a screen the recipient is reading back to "opening".
  const attemptedRef = useRef(false);
  // Single-flight, so a retry or a second ACCEPT click during a slow request cannot
  // double-POST.
  const inFlightRef = useRef(false);
  // The secret, for the length of this mount — see the header's third paragraph.
  const secretRef = useRef(null);
  // What [OK] opens: the recipient's own forecast id, from redeem or from a preview
  // that reported the copy already exists.
  const forecastIdRef = useRef(null);
  // Has ACCEPT been pressed? Decides which post `retry` repeats, and locks out a
  // decline once the copy is (or may already be) made.
  const acceptedRef = useRef(false);
  // Reached a terminal success. `openCopy` navigates on this and nothing else, so a
  // stray call cannot take someone off the offer screen.
  const doneRef = useRef(false);
  // Declined. Terminal too, but NOT a success — kept apart from `doneRef` precisely
  // so `openCopy` cannot navigate off it.
  const declinedRef = useRef(false);
  // The declined screen's auto-dismiss timer (see `reject`). Held so unmount can clear
  // it — an orphaned timer that survives the mount would navigate a page that has moved
  // on, and in a test it keeps the process alive.
  const timerRef = useRef(null);
  // Own effect with an empty dep array, so its cleanup runs on UNMOUNT ONLY.
  const aliveRef = useRef(true);
  useEffect(() => {
    aliveRef.current = true;
    return () => {
      aliveRef.current = false;
      if (timerRef.current !== null) {
        clearTimeout(timerRef.current);
        timerRef.current = null;
      }
    };
  }, []);

  /** Drop every copy of the secret. Called on a terminal outcome only. */
  const spendSecret = useCallback(() => {
    clearStashedSecret(copyId, { storage });
    secretRef.current = null;
  }, [copyId, storage]);

  // `doneRef.current || declinedRef.current` — "this visit has finished, one way or
  // the other" — is repeated inline at the top of both posts and in `reject` rather
  // than factored into a helper: a function defined in the component body is a
  // dependency of every `useCallback` that closes over it (react-hooks/exhaustive-deps
  // is a warning this app counts), while a ref read is exempt. It guards against a
  // stray call or a stale closure resurrecting an offer the recipient declined, or
  // re-running one they already completed.
  const runPreview = useCallback(async () => {
    if (inFlightRef.current || doneRef.current || declinedRef.current) return;

    if (!isCopyId(copyId)) {
      // Terminal, and nothing here can want the secret back: nothing was stashed, no
      // request was made, and no retry is offered. So take it out of the address bar —
      // and therefore out of the history entry the initial navigation wrote — rather
      // than leaving it on screen for a link that can never be redeemed.
      //
      // NOT fixed by hoisting `acquireCopySecret` above this guard, which is the
      // obvious refactor and is actively worse: acquiring first would stash a live
      // secret against a garbage copy id, and nothing would clear that entry until its
      // TTL — a longer-lived exposure than the one being closed here.
      //
      // The counter-argument, for the record, because it is not silly: mint shows the
      // URL exactly once, so in principle a recipient's address bar could hold the last
      // surviving copy of a live secret. It does not apply to THIS branch — truncation
      // eats the tail, so a clipped link damages the secret and leaves the id intact,
      // which lands on the no-secret/403 paths instead. Reaching here needs mid-string
      // mangling, and recovery would mean hand-reassembling a UUID.
      clearFragment(win);
      setPhase("needs-link");
      setNote({ message: BAD_LINK_MESSAGE, retryable: false });
      // The recipient's view of this is one sentence about a link that cannot work,
      // and their report will say exactly that. `reason` is the half they cannot see.
      //
      // Recorded here and below: the copy id, the outcome, and HTTP status. Never the
      // secret, and never the URL that carried it — the fragment is the capability,
      // and this route exists to get it out of sight. A bug bundle leaves the machine,
      // so a secret in the event log would be a live credential mailed to a triager.
      recordUiEvent({ event: "ui.redeem.blocked", reason: "bad-link" });
      return;
    }

    // Synchronous, and before anything can navigate: reads the fragment, parks it,
    // and scrubs the address bar. See acquireCopySecret for why the order matters.
    const secret = acquireCopySecret(copyId, { win, storage, now: clock() });
    if (!secret) {
      setPhase("needs-link");
      setNote({ message: NO_SECRET_MESSAGE, retryable: false });
      // The commonest confused report on this route — a link pasted without its
      // fragment, or one reopened after the stash TTL. Indistinguishable on screen
      // from a link that was never valid, which is why the two carry `reason`.
      recordUiEvent({ event: "ui.redeem.blocked", reason: "no-secret", copyId });
      return;
    }
    secretRef.current = secret;

    recordUiEvent({ event: "ui.redeem.arrive", copyId });
    inFlightRef.current = true;
    setPhase("opening");
    setNote(null);
    let r;
    try {
      // Preview WRITES NOTHING, so a refusal here costs the recipient nothing but the
      // screen they are shown. (Bundle and server ship in one container, so this
      // route can never be talking to a build that lacks the endpoint.)
      // telemetry-no-intent: the offer's preview writes nothing; ACCEPT is copy_link.redeem
      r = await apiPostImpl(`/api/copy-links/${encodeURIComponent(copyId)}/preview`, { secret });
    } finally {
      inFlightRef.current = false;
    }
    if (!aliveRef.current) return;

    if (r.ok) {
      // URL hygiene while the recipient reads the offer: a fragment they can see is a
      // fragment a screenshot or a shoulder can catch, and the deliberation is the
      // longest this screen ever stays open. Safe to scrub now — ACCEPT reads
      // `secretRef`, and the stash covers a reload.
      clearFragment(win);
      const d = previewDetails(r.body);
      setDetails(d);
      setNote(null);
      if (d.already) {
        // Terminal, and a SUCCESS: they own this copy already, so there is nothing to
        // accept and nothing left for the secret to do. The screen says so and offers
        // the way in — which is the whole reason preview asks before redeem does.
        forecastIdRef.current =
          r.body && typeof r.body.forecastId === "string" ? r.body.forecastId : null;
        spendSecret();
        doneRef.current = true;
        setPhase("done");
        recordUiEvent({ event: "ui.redeem.offer", copyId, already: true });
        return;
      }
      setPhase("offer");
      recordUiEvent({ event: "ui.redeem.offer", copyId, already: false });
      return;
    }

    const f = classifyRedeemFailure(r.status, r.body);
    if (!f.keepSecret) {
      spendSecret();
      clearFragment(win);
    }
    setPhase("failed");
    setNote({ message: f.message, retryable: f.retryable });
    // `status` and not `f.message`: the status is what separates the failures a
    // recipient describes identically ("it says it didn't work") — 410 revoked, 403
    // wrong secret, 409 not ready yet, 503 misdeployed. `keepSecret` rides along
    // because it decides whether the same URL can ever work again, which is the
    // first thing anyone answering the report needs to know.
    recordUiEvent({
      event: "ui.redeem.failed",
      at: "preview",
      copyId,
      status: r.status,
      retryable: f.retryable,
      keepSecret: f.keepSecret,
    });
  }, [copyId, win, apiPostImpl, storage, clock, spendSecret]);

  const runRedeem = useCallback(async () => {
    if (inFlightRef.current || doneRef.current || declinedRef.current) return;

    // Normally the in-memory copy from the preview. The fallback exists for the one
    // shape that can lose it — a page that came back from somewhere with the ref
    // reset — and it is a read, so it cannot resurrect a spent secret.
    const secret = secretRef.current || acquireCopySecret(copyId, { win, storage, now: clock() });
    if (!secret) {
      setPhase("needs-link");
      setNote({ message: NO_SECRET_MESSAGE, retryable: false });
      recordUiEvent({ event: "ui.redeem.blocked", reason: "no-secret", copyId });
      return;
    }
    secretRef.current = secret;

    recordUiEvent({ event: "ui.redeem.accept", copyId });
    acceptedRef.current = true;
    inFlightRef.current = true;
    setPhase("creating");
    setNote(null);
    // The recipient's redeem (P2 design §4.6): never the link or its secret; the copy's
    // forecast id is not known until the server answers.
    const intent = beginIntent("copy_link.redeem", {}, { origin: "copy-redeem.accept" });
    let r;
    try {
      r = await apiPostImpl(
        `/api/copy-links/${encodeURIComponent(copyId)}/redeem`,
        { secret },
        ...intentArgs(intent),
      );
    } catch (e) {
      intent.end("error");
      throw e;
    } finally {
      inFlightRef.current = false;
    }
    intent.end(r.ok ? "ok" : "error");
    if (!aliveRef.current) return;

    if (r.ok) {
      // Used exactly once — dropped for a fresh copy (201) and for an idempotent
      // repeat (200) alike.
      spendSecret();
      clearFragment(win);
      forecastIdRef.current =
        r.body && typeof r.body.forecastId === "string" ? r.body.forecastId : null;
      // A repeat can still surface here — two tabs, or a reload between accept and
      // reply — and the wording follows the server's answer rather than the button
      // that was pressed.
      if (r.body && r.body.alreadyRedeemed === true) {
        setDetails((prev) => ({ ...previewDetails(null), ...(prev || {}), already: true }));
      }
      doneRef.current = true;
      setPhase("done");
      setNote(null);
      // A FRESH copy hands off on its own; an ALREADY-HAD one waits for [OK].
      //
      // The asymmetry is the point. Consent for a fresh copy was given a moment ago
      // by pressing ACCEPT, so arriving at the copy is the thing that was asked for
      // and a second confirming click is friction. The already-had screen instead
      // carries news — nothing was created, the copy predates this visit — and
      // navigating past that unread is what made its wording pointless before.
      //
      // This is the ONE automatic navigation left, and it is downstream of an
      // explicit decision rather than of merely opening a URL, which is the whole
      // objection the accept/reject screen exists to answer.
      const repeat = !!(r.body && r.body.alreadyRedeemed === true);
      // `repeat` distinguishes the two 2xx outcomes that differ in what the recipient
      // then sees: a fresh copy hands off on its own, an already-had one waits for
      // [OK]. "It jumped somewhere" and "it just sat there" are the same success here.
      recordUiEvent({
        event: "ui.redeem.redeemed",
        copyId,
        repeat,
        forecastId: forecastIdRef.current,
      });
      if (!repeat && typeof onRedeemedRef.current === "function") {
        onRedeemedRef.current(forecastIdRef.current);
      }
      return;
    }

    const f = classifyRedeemFailure(r.status, r.body);
    if (!f.keepSecret) {
      spendSecret();
      clearFragment(win);
    }
    setPhase("failed");
    setNote({ message: f.message, retryable: f.retryable });
    // Worse than the preview leg's failure: ACCEPT was pressed, so the recipient
    // believes they asked for something. Whether anything was created is exactly what
    // `at` answers — nothing is written before this point.
    recordUiEvent({
      event: "ui.redeem.failed",
      at: "redeem",
      copyId,
      status: r.status,
      retryable: f.retryable,
      keepSecret: f.keepSecret,
    });
  }, [copyId, win, apiPostImpl, storage, clock, spendSecret]);

  useEffect(() => {
    if (attemptedRef.current) return;
    attemptedRef.current = true;
    void runPreview();
  }, [runPreview]);

  /** ACCEPT: the one path that creates anything. */
  const accept = useCallback(() => {
    void runRedeem();
  }, [runRedeem]);

  /**
   * REJECT: local, silent, and terminal.
   *
   * No request — there is no "declined" state on the record, and inventing one would
   * tell the owner who looked. Refused once ACCEPT has been pressed, in flight or
   * finished: the copy may already exist, and a "declined" screen over a created copy
   * would be a lie the recipient acts on.
   */
  const reject = useCallback(() => {
    if (acceptedRef.current || doneRef.current || declinedRef.current) return;
    declinedRef.current = true;
    spendSecret();
    clearFragment(win);
    setPhase("declined");
    setNote(null);
    // The one outcome with no server-side trace at all — REJECT deliberately sends no
    // request, so without this the deliberate decline and a recipient who never opened
    // the link are the same silence. It also dates the moment the secret was dropped,
    // which is the answer to "I said no and now the link won't work".
    recordUiEvent({ event: "ui.redeem.reject", copyId });
    withIntent("copy_link.reject", () => undefined, {}, { origin: "copy-redeem.reject" });
    // AND THEN LEAVE, on a timer rather than a button. The recipient has already made
    // the decision; the screen is confirming it, not asking anything, so a control to
    // acknowledge one's own choice is a click for nothing. The delay exists only so the
    // one load-bearing sentence — the link still works — can be read before the screen
    // goes, which is why the body says the return is coming (a jump with no warning
    // reads as a bug).
    //
    // The two REFUSAL screens keep their button, deliberately: there the recipient
    // decided nothing, the outcome is not what they wanted, and how long they want to
    // sit with the message is theirs to choose.
    if (timerRef.current === null && typeof setTimeout === "function") {
      timerRef.current = setTimeout(() => {
        timerRef.current = null;
        // `aliveRef` because a timer is the one thing here that can fire after the
        // component is gone; navigating from a dead mount would yank a page the
        // recipient has since moved on to.
        if (!aliveRef.current) return;
        if (typeof onDismissRef.current === "function") onDismissRef.current();
      }, DECLINE_DISMISS_MS);
    }
  }, [win, spendSecret]);

  /** [OK]: the only caller of `onRedeemed`, and only from a terminal success. */
  const openCopy = useCallback(() => {
    if (!doneRef.current) return;
    // No `copyId` here on purpose: it would join this callback's deliberately empty
    // dep array, and the redeem event above already ties this forecast id to its copy.
    recordUiEvent({ event: "ui.redeem.open_copy", forecastId: forecastIdRef.current });
    if (typeof onRedeemedRef.current === "function") onRedeemedRef.current(forecastIdRef.current);
  }, []);

  /** Repeat whichever post failed — the redeem once ACCEPT has been pressed, else the
   *  preview. Only ever reachable from a refusal that kept the secret. */
  const retry = useCallback(() => {
    // A repeated retry is a report signature in its own right: the same `at` three
    // times running says the recipient sat on a retryable failure that never cleared,
    // which reads very differently from one press that worked.
    recordUiEvent({ event: "ui.redeem.retry", at: acceptedRef.current ? "redeem" : "preview" });
    if (acceptedRef.current) {
      void runRedeem();
      return;
    }
    void runPreview();
  }, [runPreview, runRedeem]);

  return {
    phase,
    details,
    already: !!(details && details.already),
    message: note ? note.message : null,
    retryable: !!(note && note.retryable),
    accept,
    reject,
    openCopy,
    retry,
  };
}
